import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PageObjectService } from '@Core/models/proprietary/page/page-object.service';
import { AppService } from '@Core/services/proprietary/app.service';

@Component({
    standalone: false,
    selector: 'ep-edit-status-inner',
    template: `
    <div style="display:inline-flex; align-items: center; gap: var(--unity-spacing-02)">
      <unity-badge
        *ngIf="layout == 'form' && badgeText && badgeText != 'Unknown'"
        [class]="'me-1 muted ' + getStatusClass(value)"
      >
        {{ badgeText }}
      </unity-badge>
      <button unity-button
        classes="icon-only  icon-only-small"
        *ngIf="allowAddAvailable && innerValue == '?' && !app.DesignerMode"
        (click)="addAvailableItem()"
        ngbTooltip="Add"
      >
        <svg class="unity-icon unity-icon-plus">
          <use xlink:href="#unity-icon-plus"></use>
        </svg>
      </button>
      <button unity-button
        classes="icon-only icon-only-small"
        *ngIf="allowClone && !app.DesignerMode"
        (click)="cloneItem()"
        ngbTooltip="Clone"
      >
        <svg class="unity-icon unity-icon-copy">
          <use xlink:href="#unity-icon-copy"></use>
        </svg>
      </button>
      <button unity-button
        classes="icon-only icon-only-small"
        *ngIf="allowDelete && !['D', 'X'].includes(innerValue.toUpperCase()) && !app.DesignerMode"
        (click)="deleteItem()"
        ngbTooltip="Delete"
      >
        <svg class="unity-icon unity-icon-trash">
          <use xlink:href="#unity-icon-trash"></use>
        </svg>
      </button>
      <button unity-button
        classes="icon-only icon-only-small"
        *ngIf="allowRestore && ['D', 'X'].includes(innerValue.toUpperCase()) && !app.DesignerMode"
        (click)="restoreItem()"
        ngbTooltip="Restore"
      >
        <i class="fa fa-ep-restore"></i>
      </button>
      <unity-badge
        *ngIf="layout == 'row' && badgeText && badgeText != 'Unknown'"
        [class]="'ms-1 muted ' + getStatusClass(value)"
      >
        {{ badgeText }}
      </unity-badge>
    </div>
  `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditStatusInnerComponent),
            multi: true
        }
    ]
})
export class EditStatusInnerComponent implements ControlValueAccessor {
    static key = 'EditStatusInnerComponent';

    @Input('disabled')
    controlDisabled: boolean = false; //is this the right way?

    @Input()
    allowDelete: boolean = false;

    @Input()
    allowRestore: boolean = false;

    @Input()
    allowClone: boolean = false;

    @Input()
    allowAddAvailable: boolean = false;

    @Input()
    layout: string = 'form';

    @Output()
    onClone = new EventEmitter();

    @Output()
    onDelete = new EventEmitter();

    @Output()
    onRestore = new EventEmitter();

    @Output()
    onAddAvailable = new EventEmitter();

    innerValue: string = '';
    badgeText: string = '';

    constructor(private cdRef: ChangeDetectorRef, private pageOS: PageObjectService, public app: AppService) { }

    ngOnInit() { }

    cloneItem() {
        this.onClone.emit();
        this.pageOS.setDirty();
    }

    deleteItem() {
        this.onDelete.emit();
        this.pageOS.setDirty();
    }

    restoreItem() {
        this.onRestore.emit();
        this.pageOS.setDirty();
    }

    addAvailableItem() {
        this.onAddAvailable.emit();
        this.pageOS.setDirty();
    }

    setBadgeText(value: string): void {
        if (value) {
            switch (value.toString().toUpperCase()) {
                case 'N':
                    this.badgeText = null;
                    break;
                case 'A':
                    this.badgeText = 'Added';
                    break;
                case 'D':
                case 'X':
                    this.badgeText = 'Deleted';
                    break;
                case 'C':
                    this.badgeText = 'Changed';
                    break;
                case 'R':
                    this.badgeText = 'Restored';
                    break;
                case '?':
                    this.badgeText = 'Available';
                    break;
                default:
                    this.badgeText = 'Unknown';
                    break;
            }
        }
    }

    getStatusClass(value: any) {
        if (value) {
            switch (value.toString().toUpperCase()) {
                case 'A':
                case 'R':
                    return 'success';
                case '?':
                    return 'info';
                case 'C':
                    return 'warning';
                case 'D':
                case 'X':
                    return 'error';
                default:
                    return;
            }
        }
    }

    //***** BEGIN CONTROL VALUE ACCESSOR HANDLERS *****//

    //Placeholders for the callbacks which are later provided by the Control Value Accessor
    private onChangeCallback = (_: any) => { };
    private onTouchedCallback = () => { };

    //get accessor
    get value(): any {
        return this.innerValue;
    }

    ////set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.setBadgeText(v);
            this.onChangeCallback(v);
        }
    }

    //From ControlValueAccessor interface - Allows Angular to update the model
    writeValue(v: string): void {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.setBadgeText(v);
            this.cdRef.detectChanges(); //force change detection
        }
    }

    //From ControlValueAccessor interface - Allows Angular to register a function to call when the model changes.
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to register a function to call when the input has been touched.
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }

    //***** END CONTROL VALUE ACCESSOR HANDLERS *****//
}
